"use client";

import { House, SignOut } from "@phosphor-icons/react";
import * as Sentry from "@sentry/browser";
import { useEffect, useMemo, useState } from "react";
import { ErrorScreen } from "@/components/feedback/error-screen";
import DiscordReportButton from "@/components/integrations/discord/discord-report-button";
import { SignOutDialog } from "@/components/layout/sidebar/sign-out-dialog";
import { Button } from "@/components/ui/button";
import { useCurrentUserManagerSafe } from "@/hooks/use-current-user-manager";
import { useLocalizedRouter } from "@/hooks/use-localized-router";
import { useTranslation } from "@/i18n/client";
import { isChunkLoadError, reloadOnceForUpdate } from "@/utils/version";

export default function ErrorPage({ error: _error }: { error: Error & { digest?: string } }) {
  const { t } = useTranslation("global-error");
  const currentUserManager = useCurrentUserManagerSafe();
  const { navigateTo } = useLocalizedRouter();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false);

  const isChunk = useMemo(() => {
    const text = `${_error?.name ?? ""} ${_error?.message ?? ""}`;
    return isChunkLoadError(text);
  }, [_error]);
  useEffect(() => {
    if (isChunk) {
      reloadOnceForUpdate();
    }
  }, [isChunk]);

  useEffect(() => {
    if (!isChunk) {
      Sentry.captureException(_error);
    }
  }, [isChunk, _error]);

  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      if (currentUserManager) {
        await currentUserManager.signoutRedirect();
      } else {
        // If no user manager is available, redirect to sign in page
        navigateTo("/auth/signin");
      }
    } catch (_error) {
      // Fallback to redirect to sign in page
      navigateTo("/auth/signin");
    } finally {
      setIsSigningOut(false);
    }
  };

  const openSignOutDialog = () => {
    setIsSignOutDialogOpen(true);
  };

  const closeSignOutDialog = () => {
    setIsSignOutDialogOpen(false);
  };

  return isChunk ? null : (
    <>
      <ErrorScreen
        title={t("title")}
        message={t("message")}
        variant="error"
        showGoBackButton={false}
        showHomeButton={false}
      >
        <div className="w-full flex flex-col gap-3 md:gap-4">
          <div className="w-full flex flex-col md:flex-row md:justify-center gap-3 md:gap-4">
            <Button
              onClick={openSignOutDialog}
              className="w-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white border-0"
              size="lg"
              disabled={isSigningOut}
            >
              <SignOut className="mr-2 h-5 w-5" />
              {isSigningOut ? t("signingOut") : t("signOutButton")}
            </Button>
            <Button onClick={() => navigateTo("/")} className="w-full md:mt-0" size="lg">
              <House className="mr-2 h-5 w-5" />
              Ir al inicio
            </Button>
          </div>
          <div className="w-full">
            <DiscordReportButton error={_error} />
          </div>
        </div>
      </ErrorScreen>

      <SignOutDialog
        isOpen={isSignOutDialogOpen}
        onClose={closeSignOutDialog}
        onConfirm={handleSignOut}
        isSigningOut={isSigningOut}
      />
    </>
  );
}
